<template>
  <div
    :id="`${id}`"
    :class="{
      multiselectlist: true
    }">
    <div class="multiselectlist__inner">
      <h3
        class="multiselectlist__header"
        @click.prevent="onClickSelections()">
        <button
          ref="controlButton"
          type="button"
          :aria-label="listExpanded ? 'Hide selected items' : 'Show selected items'"
          class="multiselect-state-control nextgen"
          :aria-expanded="listExpanded"
          @keydown="
            (event) => onButtonKeydown('controlButton', event)
          "></button>
        <span class="multiselectlist__header-text">Selected Items</span>
        <span class="multiselectlist__header-count">{{
          selections.length
        }}</span>
        <button
          :id="`${id}_addbutton_condensed`"
          type="button"
          class="button primary condensed multiselectlist__addbutton"
          title="Add To Cart"
          aria-label="Add To Cart"
          :disabled="disabled"
          @click.prevent.stop="onAddButton()">
          <span class="ui-button-text">Add To Cart</span>
        </button>
      </h3>
      <div class="multiselectlist__content">
        <div
          ref="listItemsContainer"
          class="multiselectlist__items-container">
          <div
            ref="listItemsFocus"
            class="multiselectlist__items-focus"
            aria-hidden="true"></div>
          <!-- MR: Not sure if this is the best ARIA role to use but this will be revisited later, as part of a different accessibility task.
                   Right now it only handles keyboard navigation. -->
          <p
            ref="listItems"
            class="multiselectlist__items"
            role="region"
            aria-label="Selected Items - Scrollable content"
            @focusin="showFocusIndicator()"
            @focusout="removeFocusIndicator()"
            v-html="descriptions"></p>
        </div>
        <div class="multiselectlist__buttons">
          <button
            :id="`${id}_clearbutton`"
            type="button"
            class="button multiselectlist__clearbutton"
            title="Clear Selection"
            aria-label="Clear Selection"
            :disabled="disabled"
            @click.prevent="onClearButton()">
            <span class="ui-button-text">Clear Selection</span>
          </button>
          <button
            :id="`${id}_addbutton`"
            ref="addButton"
            type="button"
            class="button primary multiselectlist__addbutton"
            title="Add To Cart"
            aria-label="Add To Cart"
            :disabled="disabled"
            @click.prevent="onAddButton()"
            @keydown="(event) => onButtonKeydown('addButton', event)">
            <span class="ui-button-text">Add To Cart</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mutations } from '@/store/types'

export default {
  name: 'MultiSelectListComponent',
  emits: ['change', 'update:modelValue', 'submitForm'],
  props: {
    id: String,
    modelValue: Array,
    error: Boolean,
    disabled: Boolean,
    descriptions: String
  },
  data() {
    return {
      selections: [],
      listExpanded: false
    }
  },
  mounted() {
    if (this.modelValue !== this.selections) {
      this.selections = this.modelValue
      this.updateSelectionDisplay()
    }
  },
  watch: {
    modelValue: {
      handler() {
        if (this.modelValue !== this.selections) {
          this.selections = this.modelValue
        }

        this.updateSelectionDisplay()
      },
      deep: true
    },
    listExpanded() {
      if (this.listExpanded) {
        this.addClass('multiselectlist--state-open')
      } else {
        this.removeClass('multiselectlist--state-open')
      }
    }
  },
  methods: {
    updateSelectionDisplay() {
      if (this.selections.length !== 0) {
        this.addClass('multiselectlist--state-enabled')
        this.setDisplay('flex')

        // see the comments from js/shared/plugins/ui/multiselectlist.js (_toggleGroup function) on why this is needed
        this.$nextTick(() => {
          if (
            this.$refs.listItems &&
            this.$refs.listItems.scrollHeight >
              this.$refs.listItems.clientHeight
          ) {
            this.$refs.listItems.setAttribute('tabindex', 0)
          } else {
            this.$refs.listItems.removeAttribute('tabindex')
          }
        })
      } else {
        this.removeClass('multiselectlist--state-enabled')
        this.setDisplay('none')
      }
    },
    onClickSelections() {
      this.listExpanded = !this.listExpanded
    },
    addClass(value) {
      document.getElementById('app').classList.add(value)
    },
    removeClass(value) {
      document.getElementById('app').classList.remove(value)
    },
    setDisplay(value) {
      document.getElementById(this.id).style.display = value
    },
    onClearButton() {
      this.selections = []

      this.listExpanded = false
      this.updateSelectionDisplay()

      this.$emit('change', this.selections)
      this.$emit('update:modelValue', this.selections)
    },
    onAddButton() {
      if (!this.selections) {
        this.$store.commit(mutations.ADD_MESSAGE, {
          type: 'error',
          text: 'There are no selections to add to the cart.'
        })

        return
      }

      this.$emit('submitForm')
    },
    showFocusIndicator() {
      this.$refs.listItemsContainer.classList.add('focused-container')
      this.$refs.listItemsFocus.style.display = 'block'
    },
    removeFocusIndicator() {
      this.$refs.listItemsContainer.classList.remove('focused-container')
      this.$refs.listItemsFocus.style.display = 'none'
    },
    onButtonKeydown(button, ev) {
      if (ev.key === 'Tab') {
        if (
          this.listExpanded &&
          !ev.shiftKey &&
          button === 'addButton' &&
          this.$refs.controlButton.checkVisibility()
        ) {
          ev.preventDefault()
          ev.stopPropagation()
          this.$refs.controlButton.focus()
        } else if (
          this.listExpanded &&
          ev.shiftKey &&
          button === 'controlButton'
        ) {
          ev.preventDefault()
          ev.stopPropagation()
          this.$refs.addButton.focus()
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/css/abstract/variables';
@import '../assets/css/abstract/mixins';
@import '../assets/css/components/multiselectlist';

.multiselectlist {
  display: none;
  -webkit-transition: height 0.5s;
  transition: height 0.5s;
  overflow: hidden;
}

.multiselectlist__inner {
  width: 100%;

  .focused-container {
    grid-template-columns: 0.5% 99.5%;
  }
}

.multiselectlist__header {
  display: flex;
  background-position: 7px center;

  .multiselectlist__header-text {
    flex-grow: 2;
  }

  button {
    float: right;
    margin: 6px 10px 0 10px;
    display: none;
  }
}

@media @tablet-down {
  .multiselectlist__header-text {
    padding-left: 10px;
  }

  .multiselectlist__header-count {
    left: 35px;
  }

  .condensed.multiselectlist__addbutton {
    display: block;
    width: 150px;
  }

  .multiselectlist--state-open .condensed.multiselectlist__addbutton {
    display: none;
  }

  .multiselectlist__header .multiselect-state-control.nextgen {
    position: relative;
    float: left;
    margin: 0px 0px 0px 12px;
  }
}
</style>
